import * as React from 'react';
import OAuth from './oauth';
import { OAuthKey } from './oauth-key';
import { RequestResult } from './result';
import RequestParameters, { isRequestParameters } from './parameters';
import { PageVisibilityAPI } from './PageVisibility';
import axios, { CancelTokenSource, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { Dimmer, Loader, Form, FormProps  } from 'semantic-ui-react';
const _ = require('lodash');

const defaultDelay = 250;
const defaultDebounce = 750;

export interface FormSubmission {
    request?: any;
    data?: any;
    result?: RequestResult;
    updateData: (data: any) => void;
    refresh: () => void;
    backgroundRefresh: () => void;
}

interface RequestFormProps extends FormProps {
    onSubmit?: (event: React.FormEvent<HTMLFormElement>, data: FormSubmission) => void;
}

class State {
    pending: boolean;
    backgroundRefresh: boolean;
    data?: any;
    result?: RequestResult;
    key?: OAuthKey;
}

export interface DataRequestProps {
    // if set, the request will be repeated every given millis
    interval?: number;

    // if set to true, the request will be paused until set to false
    paused?: boolean;

    // if set, request will be performed on first mount, otherwise a small delay will scheduled
    delay?: boolean | number;

    // if set, request will be immediately made if data property changes
    auto?: boolean;

    // if set, will cause a spinner to show while the request is pending
    spinner?: boolean;

    // if set, the page is dimmed while the spinner is showing
    // only valid if spinner is also set
    dimmer?: boolean;

    // debounce waits a small delay (or given millis) for data property to stabilize preventing things like filter
    // changes from causing a barrage of repeated requests and cancellations.
    debounce?: number;

    // if set (default), data will not be refreshed when browser tab is not visible (if detected)
    optimize?: boolean;

    // set the component that children are rendered within, defaults to a ReactFragment (no output component)
    form?: RequestFormProps;

    // set when you want to order the output of the DataRequest. Defaults to an empty string.
    orderBy?: string;

    // set when you want to tree order the output of the DataRequest (sub-representations). Defaults to an empty string.
    treeOrder?: boolean;

    // axios request configuration
    host?: string;              // optional, will default to currently selected host
    method?: 'get'|'post'|'put'|'patch'|'delete';    // defaults to GET
    endpoint: string;           // endpoint relative to host root
    params?: RequestParameters | {};
    data?: any;
    config?: AxiosRequestConfig;
    contentType?: string;   // defaults to application/json

    // events
    transform?: (data: any) => any;
    response?: (data: any, key: OAuthKey) => void;
    error?: (error: AxiosError) => void;
    cancelled?: (config: AxiosRequestConfig) => void;
}

export interface Response {
    request?: any;
    data?: any;
    result?: RequestResult;
    spinner: boolean;           // true if spinner is being shown
    loading: boolean;           // true if we are loading data now
    refresh: () => void;
    backgroundRefresh: () => void;
}

export type RenderFunctionSignature = (response: Response) => void;
export type RefreshFunctionSignature = () => void;

export class DataRequest extends React.Component<DataRequestProps, State> {
    public static defaultProps: Partial<DataRequestProps> = {
        method: 'get',
        spinner: false,
        dimmer: false,
        debounce: 0,
        optimize: true,
        delay: false,
        auto: false,
        contentType: 'application/json; charset=utf-8',
        paused: false,
        orderBy: '',
        treeOrder: false
    };

    private updateTimer?: number;
    private debounceTimer?: number;
    private cancelToken?: CancelTokenSource;

    private refreshCallback: () => void;
    private backgroundRefreshCallback: () => void;

    handlePausedChanged() {
        if (this.props.paused) {
            this.cancelTimers();
        } else {
            this.schedule();
        }
    }
    cancel() {
        this.cancelTimers();
        this.cancelRequests();
    }

    componentWillMount() {
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.updateData = this.updateData.bind(this);
        this.setState({
            pending: false,
            data: undefined
        });
        this.onDebounceChanged();
    }

    componentDidMount() {
        const { delay } = this.props;
        PageVisibilityAPI.addListener(this.handleVisibilityChange);
        if (delay)
            this.schedule((delay === true) ? defaultDelay : delay );
        else
            this.executeImmediate(false);
    }

    componentWillUnmount() {
        this.cancel();
        PageVisibilityAPI.removeListener(this.handleVisibilityChange);
    }

    componentDidUpdate(prevProps: DataRequestProps, prevState: State) {
        const paramsChanged = !_.isEqual(prevProps.params, this.props.params);
        const dataChanged = !_.isEqual(prevProps.data, this.props.data);

        if (this.props.auto && (paramsChanged || dataChanged)) {
            // detected data change
            if (!this.props.paused)
                this.execute(false, this.props.debounce);
        }
        if (this.props.debounce !== prevProps.debounce) {
            this.onDebounceChanged();
        }
        if (this.props.interval !== prevProps.interval) {
            // interval has changed so update the update timer
            this.schedule();
        }
        if (this.props.paused !== prevProps.paused) {
            this.handlePausedChanged();
        }
    }

    handleVisibilityChange() {
        if (this.props.optimize) {
            const hidden = PageVisibilityAPI.isHidden();
            if (hidden)
                this.cancelTimers();
            else if (!this.props.paused)
                this.executeImmediate(true);
        }
    }

    renderDimmer() {
        const { pending, backgroundRefresh } = this.state;
        const { spinner, dimmer } = this.props;
        if (pending && spinner && !backgroundRefresh) {
            return dimmer
                ? <Dimmer active={pending}><Loader/></Dimmer>
                : <Loader active/>;
        } else return false;
    }

    renderContent() {
        const { pending, backgroundRefresh, result } = this.state;
        const { data, spinner, children } = this.props;

        // pull out the containing component type, or default to React.Fragment.
        // very important that the variable has first letter Capitalized or React wont recognize as a component type in JSX below!!
        // const Component = this.props.component || React.Fragment;

        // determine if we should be showing a loader
        const showLoader = (pending && spinner && !backgroundRefresh) || false;

        // render the output
        // this will depend if we are given child nodes or a function to render
        const childIsRenderFunction = (children && typeof children === 'function');
        const _output = childIsRenderFunction
            ? (children as RenderFunctionSignature)({
                request: data,
                data: this.state.data,
                result,
                spinner: showLoader,
                loading: pending,
                refresh: this.refreshCallback,
                backgroundRefresh: this.backgroundRefreshCallback
            })
            : children;

        return (
            <React.Fragment>
                {showLoader && this.renderDimmer()}
                {_output}
            </React.Fragment>
        );
    }

    onFormSubmit(event: React.FormEvent<HTMLFormElement>, data: FormProps) {
        const {form} = this.props;
        if (form && form.onSubmit)
            form.onSubmit(event, {
                request: this.props.data,
                data: this.state.data,
                updateData: this.updateData,
                refresh: this.refreshCallback,
                backgroundRefresh: this.backgroundRefreshCallback
            });
    }

    updateData(data: any) {
        this.setState( (prevState: State) => ({ ...prevState, data }));
    }

    renderAsForm() {
        const {form} = this.props;
        const formProps = {
            ...form,
            onSubmit: this.onFormSubmit     // hook the onSubmit so we can add our state and prop data
        };
        return <Form {...formProps} >{this.renderContent()}</Form>;
    }

    render() {
        return (this.props.form === undefined)
            ? this.renderContent()
            : this.renderAsForm();
    }

    private onDebounceChanged() {
        // we setup our refresh callbacks depending on the debounce setting
        if (this.props.debounce) {
            // same refresh function bound by this and backgroundRefresh flag (to show loaders or not)
            this.refreshCallback = this.execute.bind(this, false, this.props.debounce);
            this.backgroundRefreshCallback = this.execute.bind(this, this.props.debounce);
        } else {
            // same refresh function bound by this and backgroundRefresh flag (to show loaders or not)
            this.refreshCallback = this.executeImmediate.bind(this, false);
            this.backgroundRefreshCallback = this.executeImmediate.bind(this, true);
        }
    }

    private execute(backgroundRefresh: boolean, debounce: number | undefined) {
        if ((debounce === undefined || debounce === 0) && !this.props.paused)
            this.executeImmediate(backgroundRefresh);
        else if (debounce !== undefined && !this.props.paused) {
            this.cancelTimers();
            this.debounceTimer = window.setTimeout(
                () => this.executeImmediate(backgroundRefresh), (debounce > 0) ? debounce : defaultDebounce);
        }
    }

    private executeImmediate(backgroundRefresh: boolean) {
        const {
            // request parameters
            method, endpoint, contentType, params, data, config, orderBy, treeOrder,

            // callback events
            response, error, cancelled, transform
        } = this.props;

        // cancel any pending updateScan operation
        this.cancel();

        // create a new cancel token (specifically used to cancel pending requests when component unmounts)
        this.cancelToken = axios.CancelToken.source();

        const qs = (params === undefined)
            ? ''    // no parameters
            : isRequestParameters(params)
                ? '?' + (params as RequestParameters).queryString()
                : '?' + new RequestParameters(params).queryString();

        let fullConfig = {
            ...config,
            cancelToken: this.cancelToken.token,
            method: method || 'get',
            url: endpoint.startsWith('http')
                ? endpoint + qs
                : 'http://default/' + endpoint + qs,
            contentType,
            data
        };

        if (orderBy !== '') {
            fullConfig = {
                ...fullConfig,
                headers: {'x-orderby': orderBy}
            };
        }

        if (treeOrder) {
            fullConfig = {
                ...fullConfig,
                headers: {'x-treeorder': treeOrder}
            };
        }

        // indicate we are pending
        this.setState( (prevState) => ({ ...prevState, pending: true, backgroundRefresh }) );
        axios(fullConfig)
            .then((resp: AxiosResponse) => {
                this.cancelToken = undefined;
                if (resp && resp.data !== undefined) {
                    const { key } = OAuth.getContextFromResponseObject(resp);

                    const result = RequestResult.fromAxiosResponse(resp);
                    if (result.code !== 0) {
                        result.toastDefault();
                        if (result.abortable()) {
                            this.setState((prevState) => ({ ...prevState,
                                data: undefined,
                                result,
                                pending: false,
                                backgroundRefresh
                            }));
                            this.schedule();
                            return;
                        }
                    }

                    // transform the data if a transformer exists, otherwise it just takes the data untransformed
                    const transformedData = (typeof transform === 'function') && transform(resp.data) || resp.data;

                    // todo: we could interpret the API SUCCESS code here
                    this.setState((prevState) => ({ ...prevState,
                        data: transformedData,
                        result,
                        pending: false,
                        backgroundRefresh
                    }),
                        // notify parent if callback event is set
                                  (response && key) ? () => response(transformedData, key) : undefined
                    );
                }
                this.schedule();
            })
            .catch( (err: AxiosError) => {
                const wasCancelled = axios.isCancel(err);
                this.cancelToken = undefined;
                if (!wasCancelled) {
                    // console.error('DataFetch error: ', err);
                    this.setState((prevState) => ({ ...prevState,
                        data: undefined,
                        result: RequestResult.fromAxiosError(err),
                        pending: false,
                        backgroundRefresh
                    }),
                        // notify parent if callback event is set
                                  error ? () => error(err) : undefined
                    );
                } else {
                    // console.log('DataRequest cancelled');
                    this.setState((prevState) => ({ ...prevState,
                        pending: false,
                        backgroundRefresh
                    }),
                        // notify parent if callback event is set
                                  cancelled ? () => cancelled(fullConfig) : undefined
                    );
                }
                this.schedule();
            });

    }

    private cancelTimers() {
        if (this.debounceTimer !== undefined) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = undefined;
        }
        if (this.updateTimer !== undefined) {
            clearTimeout(this.updateTimer);
            this.updateTimer = undefined;
        }
    }

    private cancelRequests() {
        // cancel any pending requests
        if (this.cancelToken !== undefined) {
            this.cancelToken.cancel();
            this.cancelToken = undefined;
        }
    }

    private schedule(millis?: number) {
        if (this.props.interval === undefined && millis === undefined)
            return; // invalid schedule call

        /*if (millis === undefined || millis === true)
            millis = this.props.interval || defaultDelay;
        else if (millis === false || isNaN(millis))
            return;
*/
        // cancel any pending timers and schedule next execution if a request isn't already in progress
        this.cancelTimers();

        if (this.state.pending || this.props.paused) {
            return;
        }
        this.updateTimer = window.setTimeout(
            () => this.executeImmediate(true),    // dont show any loaders
            millis || this.props.interval);
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/api/request.tsx